import React, { forwardRef } from 'react';
import './Input.scss';

export interface InputProps {
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search' | 'date' | 'time' | 'datetime-local' | 'select' | 'textarea' | 'checkbox' | 'price';
  placeholder?: string;
  value?: string | number;
  defaultValue?: string | number;
  name?: string;
  id?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  variant?: 'default' | 'success' | 'warning' | 'error';
  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  autoComplete?: string;
  autoFocus?: boolean;
  maxLength?: number;
  minLength?: number;
  max?: string;
  min?: string;
  step?: string;
  pattern?: string;
  title?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  className?: string;
  lang?: string;
  checked?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type = 'text',
      placeholder,
      value,
      defaultValue,
      name,
      id,
      size = 'md',
      variant = 'default',
      disabled = false,
      readonly = false,
      required = false,
      autoComplete,
      autoFocus = false,
      maxLength,
      minLength,
      max,
      min,
      step,
      pattern,
      title,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedby,
      className = '',
      lang,
      onChange,
      onBlur,
      onFocus,
      onKeyDown,
      onKeyUp,
      checked,
    },
    ref
  ) => {
    const baseClass = 'input';
    const sizeClass = `input--${size}`;
    const variantClass = `input--${variant}`;
    const stateClass = disabled ? 'input--disabled' : readonly ? 'input--readonly' : '';

    // if className is empty, add the input default classes
    const classes = className === '' ? [
      baseClass,
      sizeClass,
      variantClass,
      stateClass,
      className,
    ]
      .filter(Boolean)
      .join(' ') : className;


    return (
      <input
        ref={ref}
        type={type}
        className={classes}
        placeholder={placeholder}
        value={value}
        defaultValue={defaultValue}
        name={name}
        id={id}
        disabled={disabled}
        readOnly={readonly}
        required={required}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        maxLength={maxLength}
        minLength={minLength}
        max={max}
        min={min}
        step={step}
        pattern={pattern}
        title={title}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedby}
        aria-disabled={disabled}
        aria-readonly={readonly}
        lang={lang}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        checked={checked}
      />
    );
  }
);

Input.displayName = 'Input';

export default Input; 